import React, { useCallback, useEffect, useState } from 'react';
import { imageUrl, listFavoriteProducts } from '../../api/endpoints';
import ModalShell from '../../components/ModalShell';
import { IconBalance } from '../../components/icons/AnalyzerIcons';
import type { FavoriteProduct } from '../../types/api';
import { areSameProductReferences, createProductReference, type ProductReference } from '../../utils/productIdentity';
import { getErrorMessage } from '../../utils/errors';
import { labelSourceMarker } from '../../utils/labelSource';

export function FavoriteProductPicker({
  targetLabel,
  excludedProduct,
  onSelect,
  onClose,
}: {
  targetLabel: string;
  excludedProduct?: ProductReference;
  onSelect: (product: FavoriteProduct) => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState<FavoriteProduct[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFavorites = useCallback(async (offset: number, append: boolean) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const response = await listFavoriteProducts({ limit: 20, offset });
      setItems((current) => append ? [...current, ...response.items] : response.items);
      setNextOffset(response.next_offset ?? null);
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to load favorites'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void loadFavorites(0, false);
  }, [loadFavorites]);

  return (
    <ModalShell title="Choose from favorites" subtitle={targetLabel} onClose={onClose} widthClass="max-w-xl">
      <div className="p-4 sm:p-5 stack-3">
        {loading && <p className="t-small t-mute">Loading favorites…</p>}
        {error && <div className="notice notice--error">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="card card--bordered" style={{ padding: 'var(--s-4)', textAlign: 'center' }}>
            <p className="t-small" style={{ fontWeight: 600 }}>No favorite products yet</p>
            <p className="t-small t-mute">Save products first, then return here to select them.</p>
          </div>
        )}
        {items.map((product) => {
          const reference = createProductReference(product);
          const selected = areSameProductReferences(reference, excludedProduct);
          const pending = product.ingredients_state === 'awaiting_team';
          const disabled = selected || pending;
          const title = [product.brand, product.name].filter(Boolean).join(' ');
          const label = selected
            ? `${title} already selected`
            : pending ? `${title}: ingredients still being checked` : `Use ${title} for ${targetLabel}`;
          const src = product.image_url ? imageUrl(product.image_url) : null;
          return (
            <button
              key={product.product_id}
              type="button"
              className="card card--bordered"
              style={{
                padding: 'var(--s-3)', display: 'flex', alignItems: 'center', gap: 'var(--s-3)',
                textAlign: 'left', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              disabled={disabled}
              onClick={() => { onSelect(product); }}
              aria-label={label}
            >
              <span
                style={{
                  width: 52, height: 52, flexShrink: 0, borderRadius: 'var(--r-sm)', overflow: 'hidden',
                  background: 'var(--c-cream-weak)', display: 'grid', placeItems: 'center',
                }}
              >
                {src
                  ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <IconBalance />}
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="t-small" style={{ display: 'block', fontWeight: 600, color: 'var(--ink)' }}>
                  {product.name}
                </span>
                <span className="t-small t-mute" style={{ display: 'block' }}>{product.brand}</span>
                {labelSourceMarker(product.label_source) && <span className="pill">{labelSourceMarker(product.label_source)}</span>}
                <span className="t-small t-mute" style={{ display: 'block' }}>
                  {product.barcode ? `Barcode ${product.barcode}` : `Saved product #${product.product_id}`}
                </span>
              </span>
              <span className={disabled ? 'pill pill--coral' : 'pill pill--teal'}>
                {selected ? 'Already selected' : pending ? 'Checking ingredients' : 'Choose'}
              </span>
            </button>
          );
        })}
        {nextOffset != null && (
          <button
            type="button"
            className="btn btn--ghost btn--sm w-full justify-center"
            disabled={loadingMore}
            onClick={() => { void loadFavorites(nextOffset, true); }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </ModalShell>
  );
}
